import { Link } from "react-router-dom";
import { Instagram, Youtube, Send, MessageCircle, Linkedin, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

/* Footer — Figma node 102:3388. Shared across every public page, so route
   targets are preserved from the previous footer even where the Figma labels
   differ slightly. */

const EXPLORE_LINKS = [
  { label: "Courses", to: "/courses" },
  { label: "Package", to: "/package" },
  { label: "Upcoming", to: "/upcoming" },
  { label: "Partner Program", to: "/partner" },
  { label: "Skill Paths", to: "/#skill-paths" },
  { label: "Community", to: "/#community" },
  { label: "Resources", to: "/#resources" },
];

const COMPANY_LINKS = [
  { label: "About Us", to: "/about" },
  { label: "Free Affiliate", to: "/freeaffiliate" },
  { label: "Contact Us", to: "/contact" },
  { label: "Careers", to: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Refund Policy", to: "/refund" },
];

const SOCIALS = [Instagram, Youtube, Send, MessageCircle, Linkedin];

function LinkColumn({ title, links }) {
  return (
    <div className="flex flex-col gap-6 lg:gap-8">
      <p className="font-poppins text-[22px] leading-6 text-black lg:text-[28px]">{title}</p>
      <div className="flex flex-col gap-3">
        {links.map((l) => (
          <Link
            key={l.label}
            to={l.to}
            className="font-poppins text-[16px] leading-6 text-[#6E6D6D] transition-colors hover:text-black lg:text-[18px]"
          >
            {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function SiteFooter() {
  return (
    <footer id="footer" className="mt-10 bg-[#F8F8F8]">
      <div className="px-6 py-12 sm:px-10 lg:px-14 lg:py-16">
        <div className="mx-auto grid max-w-[1340px] gap-12 lg:grid-cols-[268px_1fr] lg:gap-[58px]">

          <div className="flex flex-col justify-end gap-8">
            {/* Footer sits on #F8F8F8, so the dark wordmark ("light" variant) is the readable one. */}
            <Link to="/" aria-label="GENRYZ home" className="flex items-center">
              <Logo className="h-12" />
            </Link>
            <p className="max-w-[268px] font-poppins text-[16px] leading-6 text-[#6E6D6D]">
              Empowering the next generation with skills, mentorship, community, and opportunities.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {SOCIALS.map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="GENRYZ social link"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[#A7A7A7] text-[#6E6D6D] transition-colors hover:border-black hover:text-black"
                >
                  <Icon size={24} />
                </a>
              ))}
            </div>
          </div>

          {/* Two columns even on the narrowest phones — these lists are short, and
              stacking all four makes the footer an unreasonably long scroll. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:gap-x-10 lg:grid-cols-[169px_169px_169px_301px] lg:gap-[58px]">
            <LinkColumn title="Explore" links={EXPLORE_LINKS} />
            <LinkColumn title="Company" links={COMPANY_LINKS} />
            <LinkColumn title="Legal" links={LEGAL_LINKS} />

            <div className="col-span-2 flex flex-col gap-7 lg:col-span-1">
              <div className="flex flex-col gap-4">
                <p className="font-poppins text-[22px] leading-6 text-black lg:text-[28px]">Stay Updated</p>
                <p className="max-w-[301px] font-poppins text-[16px] leading-6 text-[#6E6D6D] lg:text-[18px]">
                  Subscribe to get updates on new courses and free resources.
                </p>
              </div>

              {/* TODO: wire to a newsletter endpoint — still a no-op, as in the previous footer. */}
              <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-[5px]">
                <input
                  type="email"
                  placeholder="Enter your email"
                  aria-label="Email address"
                  className="h-11 w-full max-w-[248px] rounded-[24px] border-2 border-black bg-white px-4 text-center font-poppins text-[14px] tracking-[-0.084px] text-black outline-none placeholder:text-black/70 focus:border-black"
                />
                <button
                  type="submit"
                  aria-label="Subscribe"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-black bg-[var(--lime)] transition-transform hover:scale-105 active:scale-95"
                >
                  <ArrowUpRight size={20} className="text-black" />
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-14 max-w-[1340px] border-t border-black/10 pt-6">
          <p className="font-poppins text-[13px] text-[#6E6D6D]">
            © {new Date().getFullYear()} Genryz. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
